import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { colors } from "../theme";
import api from "../api";

export default function VictoryCardPage() {
  const { betId } = useParams<{ betId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // VEN-39 round 3: outcome now lives in the URL *path* (`/victory` vs
  // `/defeat`) so it survives any Telegram link-preview collapse. Round 2's
  // `?from=…` query param is kept as a backward-compat fallback so shared
  // links already out in the wild still render the right copy.
  const [searchParams] = useSearchParams();
  const isLoser =
    location.pathname.endsWith("/defeat") ||
    searchParams.get("from") === "loser";
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [bet, setBet] = useState<any>(null);

  useEffect(() => {
    if (!betId) return;

    // Victory card image endpoint is public (returns PNG, no auth needed).
    // Use it directly as an <img> src — no need to fetch via axios.
    const victoryCardUrl = `/api/bets/${betId}/victory-card`;
    setImageUrl(victoryCardUrl);

    // Load bet data — try authenticated endpoint first, fall back to
    // the public endpoint for unauthenticated visitors (VEN-39: shared
    // links open in a regular browser without Telegram auth context).
    const fetchBet = async () => {
      try {
        const res = await api.get(`/bets/${betId}`);
        setBet(res.data);
      } catch {
        try {
          const res = await api.get(`/bets/${betId}/public`);
          const d = res.data;
          // Normalize public response shape to match what the card expects
          setBet({
            ...d,
            amount: d.amount_ton != null ? d.amount_ton * 1_000_000_000 : 0,
          });
        } catch {
          // Both endpoints failed — card will show generic fallback
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBet();
  }, [betId]);

  const outcomePath = isLoser ? "defeat" : "victory";
  const pageUrl = `https://sidebets.vercel.app/bet/${betId}/${outcomePath}`;
  const shareText = isLoser
    ? "I just lost a bet on SideBets — want a rematch?"
    : "I just won a bet on SideBets!";

  const handleSendToGroup = () => {
    const tg = window.Telegram?.WebApp;
    const tgShareUrl = `https://t.me/share/url?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(shareText)}`;
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(tgShareUrl);
    } else {
      window.open(tgShareUrl, "_blank", "noopener");
    }
  };

  const handleShareImage = () => {
    if (navigator.share) {
      navigator.share({ url: pageUrl, text: shareText }).catch(() => {});
    } else {
      navigator.clipboard.writeText(pageUrl);
    }
  };

  return (
    <div style={{
      display: "flex", flexDirection: "column", height: "100%", background: colors.bg,
    }}>
      {/* Close */}
      <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 16px" }}>
        <button onClick={() => navigate(`/bet/${betId}`)} style={{ fontSize: 18, color: colors.gray, padding: 4 }}>
          ✕
        </button>
      </div>

      <div style={{
        flex: 1, overflow: "auto", padding: "0 16px 32px",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      }}>
        {loading ? (
          <div style={{ color: colors.grayLight }}>Loading victory card...</div>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt="Victory Card"
            style={{ width: "100%", maxWidth: 600, borderRadius: 16, marginBottom: 24 }}
            onError={() => setImageUrl(null)}
          />
        ) : (
          <div style={{
            width: "100%", maxWidth: 600,
            background: colors.surface, borderRadius: 16, marginBottom: 24,
            padding: 32,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            border: `1px solid ${colors.border}`,
          }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>{isLoser ? "🤡" : "🏆"}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: colors.white, marginBottom: 8 }}>
              {isLoser ? "Defeat!" : "Victory!"}
            </div>
            {bet && (
              <>
                <div style={{ fontSize: 15, color: colors.grayLight, textAlign: "center", marginBottom: 16 }}>
                  {bet.description}
                </div>
                {bet.amount && (
                  <div style={{ fontSize: 20, fontWeight: 700, color: isLoser ? "#EF4444" : colors.primary }}>
                    {isLoser ? "-" : "+"}{(bet.amount / 1_000_000_000).toFixed(2)} TON
                  </div>
                )}
              </>
            )}
            <div style={{ fontSize: 13, color: colors.grayLight, marginTop: 16 }}>
              {isLoser ? "Time for a rematch 🔥" : "Winner takes all 💪"}
            </div>
          </div>
        )}

        {/* Buttons */}
        <button onClick={handleSendToGroup} style={{
          width: "100%", padding: "14px 0", borderRadius: 14, border: "none",
          background: colors.primary, color: colors.bg,
          fontSize: 15, fontWeight: 700, marginBottom: 12, cursor: "pointer",
        }}>
          🔥 Send to Group
        </button>

        <button onClick={handleShareImage} style={{
          width: "100%", padding: "14px 0", borderRadius: 14,
          background: colors.surface, color: colors.white,
          border: `1px solid ${colors.borderLight}`,
          fontSize: 15, fontWeight: 700, cursor: "pointer",
        }}>
          ↗️ Share Image
        </button>
      </div>
    </div>
  );
}
